import React, { useMemo, useState } from 'react'
import { useGameStore } from '../../state/gameStore'
import { appBus } from '../../../app/app-events'
import { CanonicalCut } from '../../../core/cuts'
import { parseRational } from '../../../core/rational'
import { Check, Keyboard, Plus, Sliders, X } from 'lucide-react'
import clsx from 'clsx'

const formatCutTypeLabel = (type?: string): string => {
  if (!type) return 'Cut'
  const key = type.toLowerCase()
  if (key === 'gomory') return 'Gomory'
  if (key === 'gmi') return 'GMI'
  if (key === '2dsimplex') return '2D Simplex'
  if (key === 'user') return 'Manual'
  return type.charAt(0).toUpperCase() + type.slice(1)
}

const SimpleCutFormula: React.FC<{ cut: CanonicalCut }> = ({ cut }) => {
  const terms: React.ReactNode[] = []
  ;(cut.alpha || []).forEach((coeff, i) => {
    const val = Math.round(coeff * 100) / 100
    if (Math.abs(val) < 0.01) return
    const vars = ['x', 'y', 'z']
    const v = vars[i] || `x${i + 1}`

    const isFirst = terms.length === 0
    const sign = val < 0 ? '-' : isFirst ? '' : '+'
    const absVal = Math.abs(val)
    const coeffStr = absVal === 1 ? '' : absVal

    terms.push(
      <span key={i} className="mx-[1px]">
        {sign && <span className="text-slate-500 mr-[1px]">{sign}</span>}
        {coeffStr}
        <span className="font-bold italic text-white">{v}</span>
      </span>,
    )
  })

  const rhs = Math.round(cut.beta * 100) / 100

  return (
    <span className="font-mono text-lg sm:text-xl text-white font-bold leading-relaxed tracking-tight break-words block w-full">
      {terms}
      <span className="text-slate-400 mx-1">≤</span>
      {rhs}
    </span>
  )
}

type ManualParseResult = {
  cuts?: CanonicalCut[]
  error?: string
}

const parsePointToken = (token: string): number | null => {
  try {
    const value = parseRational(token).toNumber()
    return Number.isFinite(value) ? value : null
  } catch {
    return null
  }
}

const parseManualCutInput = (input: string): ManualParseResult => {
  const raw = input.trim()
  if (!raw) return { error: 'Manual cut is empty.' }

  if (raw.includes('<=') || raw.includes('≤')) {
    const norm = raw.replace(/≤/g, '<=')
    const parts = norm.split('<=')
    if (parts.length !== 2) {
      return { error: 'Manual inequality must contain a single "<=" (e.g. "x + y <= 2").' }
    }

    const lhs = parts[0].replace(/\s+/g, '')
    const rhsStr = parts[1].trim()
    let beta: number
    try {
      beta = parseRational(rhsStr).toNumber()
    } catch {
      return { error: 'Right-hand side must be a valid rational number.' }
    }
    if (!Number.isFinite(beta)) {
      return { error: 'Right-hand side must be a finite number.' }
    }

    const coeffs: Record<'x' | 'y' | 'z', number> = { x: 0, y: 0, z: 0 }
    if (!lhs) {
      return { error: 'Left-hand side must contain at least one variable term.' }
    }

    const normalized = lhs.replace(/-/g, '+-')
    const terms = normalized
      .split('+')
      .map((t) => t.trim())
      .filter(Boolean)

    for (const term of terms) {
      const variable = term[term.length - 1]?.toLowerCase()
      if (!variable || !['x', 'y', 'z'].includes(variable)) {
        return { error: `Unable to parse term "${term}". Use forms like "x", "-2y", "1/2z".` }
      }

      let coeffStr = term.slice(0, -1)
      if (!coeffStr || coeffStr === '+' || coeffStr === '-') {
        coeffStr = coeffStr === '-' ? '-1' : '1'
      }

      let coeff: number
      try {
        coeff = parseRational(coeffStr).toNumber()
      } catch {
        return { error: `Coefficient in term "${term}" is not a valid rational number.` }
      }
      if (!Number.isFinite(coeff)) {
        return { error: `Coefficient in term "${term}" is not finite.` }
      }

      coeffs[variable as 'x' | 'y' | 'z'] += coeff
    }

    const alpha = [coeffs.x, coeffs.y, coeffs.z]
    if (alpha.every((a) => Math.abs(a) < 1e-12)) {
      return { error: 'Manual cut is degenerate: all coefficients are zero.' }
    }

    return {
      cuts: [
        {
          alpha,
          beta,
          efficacy: 0,
          sourceRow: -1,
          cgfType: 'user',
        },
      ],
    }
  }

  const coordTokens = raw
    .replace(/[()]/g, ' ')
    .split(/[;,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)

  if (coordTokens.length < 9) {
    return {
      error:
        'Failed to parse three 3D points. Use format like "(0,0,0),(1,0,0),(0,1,0)" or "0,0,0; 1,0,0; 0,1,0".',
    }
  }

  const coords = coordTokens.slice(0, 9).map(parsePointToken)
  if (coords.some((value) => value === null)) {
    return { error: 'Point coordinates must be valid rational numbers.' }
  }

  const [x1, y1, z1, x2, y2, z2, x3, y3, z3] = coords as number[]

  const e1x = x2 - x1
  const e1y = y2 - y1
  const e1z = z2 - z1
  const e2x = x3 - x1
  const e2y = y3 - y1
  const e2z = z3 - z1

  const nx = e1y * e2z - e1z * e2y
  const ny = e1z * e2x - e1x * e2z
  const nz = e1x * e2y - e1y * e2x
  const normSq = nx * nx + ny * ny + nz * nz
  if (normSq < 1e-10) {
    return { error: 'The three points are nearly collinear; cannot define a unique plane.' }
  }

  const beta = nx * x1 + ny * y1 + nz * z1
  const alpha = [nx, ny, nz]

  return {
    cuts: [
      {
        alpha,
        beta,
        efficacy: 0,
        sourceRow: -1,
        cgfType: 'user',
      },
      {
        alpha: alpha.map((a) => -a),
        beta: -beta,
        efficacy: 0,
        sourceRow: -1,
        cgfType: 'user',
      },
    ],
  }
}

const CutManager: React.FC = () => {
  const { phase, availableCuts, selectedCut, selectedCutIndex, muValue, lastError } = useGameStore((state) => state.ui.cutWorkflow)
  const { phase: bnbPhase, commandLocked } = useGameStore((state) => state.ui.bnbState)
  const addNotification = useGameStore((state) => state.addNotification)
  const [customInput, setCustomInput] = useState('')
  const [selectedType, setSelectedType] = useState<string>('all')

  const bnbActive = ['active', 'selecting', 'branching', 'backtracking'].includes(bnbPhase)
  const isBusy = phase === 'applying' || bnbActive || commandLocked

  const cutTypes = useMemo(() => {
    const set = new Set<string>()
    availableCuts.forEach((c) => c.cgfType && set.add(c.cgfType.toLowerCase()))
    const types = Array.from(set)
    const order = ['gomory', 'gmi', '2dsimplex', 'user']
    types.sort((a, b) => {
      const idxA = order.indexOf(a)
      const idxB = order.indexOf(b)
      if (idxA !== -1 && idxB !== -1) return idxA - idxB
      if (idxA !== -1) return -1
      if (idxB !== -1) return 1
      return a.localeCompare(b)
    })
    return ['all', ...types]
  }, [availableCuts])

  const sortedCuts = useMemo(() => [...availableCuts].sort((a, b) => b.efficacy - a.efficacy), [availableCuts])

  const filteredCuts = useMemo(
    () => sortedCuts.filter((c) => selectedType === 'all' || (c.cgfType || '').toLowerCase() === selectedType),
    [sortedCuts, selectedType],
  )

  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isBusy) return

    const trimmed = customInput.trim()
    if (!trimmed) return

    const { cuts, error } = parseManualCutInput(trimmed)
    if (error || !cuts || cuts.length === 0) {
      addNotification(error || 'Failed to parse manual cut.', 'error')
      return
    }

    cuts.forEach((cut) => {
      appBus.emit('user-cut-added', { cut })
    })

    setCustomInput('')
    addNotification('Manual cut added to Available Cuts.', 'success')
  }

  const supportsMuAdjust = !!selectedCut?.userAdjustable

  return (
    <div className="h-full flex flex-col gap-3">
      <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar pb-1 flex-shrink-0">
        {cutTypes.map((type) => (
          <button
            key={type}
            onClick={() => setSelectedType(type)}
            className={clsx(
              'px-3 py-1.5 rounded-md text-xs font-bold whitespace-nowrap transition-all border',
              selectedType === type
                ? 'bg-slate-200 text-slate-900 border-white shadow-md'
                : 'bg-slate-800/50 text-slate-400 border-white/10 hover:bg-slate-700 hover:border-white/30 hover:text-white',
            )}
          >
            {type === 'all' ? 'All' : formatCutTypeLabel(type)}
          </button>
        ))}
      </div>

      <section className="min-h-0 rounded-lg border border-white/10 bg-slate-900/40 p-3 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-bold text-slate-300 tracking-wider">Candidates</h4>
          <span className="text-sm text-white font-mono font-bold">{filteredCuts.length}</span>
        </div>

        <div className="flex flex-col gap-2 overflow-y-auto custom-scrollbar pr-1 min-h-0">
          {filteredCuts.length === 0 && (
            <div className="text-xs text-slate-500 italic py-8 text-center border border-dashed border-white/10 rounded-lg">
              No {selectedType === 'all' ? 'valid' : formatCutTypeLabel(selectedType)} cuts found.
            </div>
          )}

          {filteredCuts.map((cut, idx) => {
            const originalIndex = availableCuts.indexOf(cut)
            const isSelected = selectedCutIndex === originalIndex
            return (
              <button
                key={`${cut.cgfType}-${idx}-${originalIndex}`}
                onClick={() => !isBusy && appBus.emit('cut-select-requested', { cutIndex: originalIndex })}
                disabled={isBusy}
                className={clsx(
                  'group flex flex-col gap-2.5 p-3 rounded-lg transition-all text-left shadow-sm flex-shrink-0 border',
                  isSelected
                    ? 'bg-slate-800 border-cyan-400/50 shadow-cyan-500/20'
                    : 'bg-slate-800/40 hover:bg-slate-800 border-white/5 hover:border-white/20',
                  isBusy && 'opacity-60 cursor-not-allowed',
                )}
              >
                <div className="flex items-start justify-between w-full gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-white bg-slate-800 px-2 py-0.5 rounded border border-white/20">#{idx + 1}</span>
                    <span
                      className={clsx(
                        'text-xs font-bold px-2 py-0.5 rounded border',
                        cut.cgfType?.toLowerCase() === 'gomory' && 'bg-sky-500/10 text-sky-200 border-sky-500/30',
                        cut.cgfType?.toLowerCase() === 'gmi' && 'bg-amber-500/10 text-amber-200 border-amber-500/30',
                        cut.cgfType?.toLowerCase() === '2dsimplex' && 'bg-emerald-500/10 text-emerald-200 border-emerald-500/30',
                        cut.cgfType?.toLowerCase() === 'user' && 'bg-fuchsia-500/10 text-fuchsia-200 border-fuchsia-500/30',
                      )}
                    >
                      {formatCutTypeLabel(cut.cgfType) || 'Cut'}
                    </span>
                  </div>
                  <span className="text-xs font-bold text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 px-2 py-0.5 rounded whitespace-nowrap">
                    Efficacy {cut.efficacy.toFixed(2)}
                  </span>
                </div>

                <div className="w-full text-center px-1">
                  <SimpleCutFormula cut={cut} />
                </div>
              </button>
            )
          })}
        </div>
      </section>

      {selectedCut && (
        <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
          <div className="text-xs text-slate-400 mb-2">Selected Cut</div>
          <SimpleCutFormula cut={selectedCut} />

          {supportsMuAdjust && (
            <div className="rounded-lg border border-cyan-500/25 bg-cyan-500/5 p-3 mt-3">
              <label className="text-xs text-cyan-200 font-bold flex items-center gap-1">
                <Sliders size={12} /> Mixing Factor (μ)
              </label>
              <input
                type="range"
                min="0.01"
                max="0.99"
                step="0.01"
                value={muValue ?? 0.5}
                onChange={(e) => !isBusy && appBus.emit('cut-mu-change-requested', { mu: parseFloat(e.target.value) })}
                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-500 mt-2"
                disabled={isBusy}
              />
              <div className="mt-1 text-[11px] text-slate-300 font-mono">μ = {(muValue ?? 0.5).toFixed(2)}</div>
            </div>
          )}

          <div className="flex gap-2 mt-3">
            <button
              onClick={() => appBus.emit('cut-confirm-requested', undefined)}
              disabled={!selectedCut || isBusy}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check size={14} /> {phase === 'applying' ? 'Applying...' : 'Confirm'}
            </button>
            <button
              onClick={() => appBus.emit('cut-cancel-requested', undefined)}
              disabled={!selectedCut || isBusy}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={14} /> Cancel
            </button>
          </div>

          {lastError && phase === 'error' && (
            <div className="mt-3 rounded-lg border border-red-500/40 text-red-200 bg-red-500/10 p-2 text-xs font-medium">{lastError}</div>
          )}
        </div>
      )}

      {!selectedCut && (
        <div className="text-xs text-slate-500 italic border border-dashed border-white/10 rounded-lg p-3">
          Select a cut card to preview/apply it.
        </div>
      )}

      <div className="p-3 bg-slate-800/60 rounded-xl border border-white/10 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <Keyboard size={14} className="text-sky-400" />
          <h4 className="text-sm font-bold text-sky-400 tracking-wide">Manual Entry</h4>
        </div>
        <form onSubmit={handleCustomSubmit} className="flex gap-2">
          <input
            type="text"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            placeholder="e.g. 1/2x + y <= 2"
            className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/20 transition-all font-mono"
            disabled={isBusy}
          />
          <button
            type="submit"
            disabled={!customInput.trim() || isBusy}
            className="px-3 py-2 bg-sky-600 hover:bg-sky-500 text-white border border-white/10 rounded-lg text-xs font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-sky-900/20"
          >
            <Plus size={16} />
          </button>
        </form>
      </div>

      {bnbActive && (
        <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded p-2">
          Branch & Bound is active. Cut actions are temporarily disabled.
        </div>
      )}

    </div>
  )
}

export default CutManager
